import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Tag, RefreshCw, Mail, Phone, Building2, ArrowRight } from 'lucide-react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { useToast } from '../../../components/ui/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateContactModal from '../../../components/admin/crm/CreateContactModal';
import PromoteCounterpartyModal from '../../../components/admin/PromoteCounterpartyModal';
import AlsoBadge from '../../../components/admin/AlsoBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contacts = () => {
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [promoteSource, setPromoteSource] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    fetchContacts();
    fetchCount();
  }, [currentPage]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const skip = (currentPage - 1) * pageSize;
      
      const response = await axios.get(`${API_URL}/api/crm/contacts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { skip, limit: pageSize }
      });
      
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch contacts",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/crm/contacts/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTotalCount(response.data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      fetchContacts();
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.post(
        `${API_URL}/api/crm/contacts/search?search_term=${encodeURIComponent(searchTerm)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setContacts(response.data);
    } catch (error) {
      console.error('Error searching contacts:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to search contacts",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (contactId, contactName) => {
    if (!window.confirm(`Delete contact "${contactName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/api/crm/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Success",
        description: "Contact deleted successfully",
      });
      
      fetchContacts();
      fetchCount();
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete contact",
      });
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowCreateModal(true);
  };

  const handleModalClose = () => {
    setShowCreateModal(false);
    setEditingContact(null);
  };

  const handleModalSuccess = () => {
    fetchContacts();
    fetchCount();
    handleModalClose();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <BackButton />

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42]">Contacts</h1>
          <p className="text-gray-600 mt-1">Manage your contact database</p>
        </div>
        
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-[#00B4D8] to-[#023E8A] hover:from-[#0096c7] hover:to-[#012a5e] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Contact
        </Button>
      </div>

      {/* Search & Filters */}
      <Card className="p-4 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, phone, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            />
          </div>
          
          <Button
            onClick={handleSearch}
            className="bg-[#00B4D8] hover:bg-[#0096c7] text-white"
          >
            Search
          </Button>
          
          <Button
            onClick={() => {
              setSearchTerm('');
              fetchContacts();
            }}
            variant="outline"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Contacts</p>
              <p className="text-2xl font-bold text-[#023E8A]">{totalCount}</p>
            </div>
            <Building2 className="w-10 h-10 text-[#00B4D8]" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Page</p>
              <p className="text-2xl font-bold text-green-700">{currentPage} / {totalPages || 1}</p>
            </div>
            <Search className="w-10 h-10 text-green-600" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Showing</p>
              <p className="text-2xl font-bold text-purple-700">{contacts.length}</p>
            </div>
            <RefreshCw className="w-10 h-10 text-purple-600" />
          </div>
        </Card>
      </div>

      {/* Contacts Table */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading contacts...</div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            {searchTerm ? 'No contacts found matching your search.' : 'No contacts yet. Create your first contact to get started.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-[#00B4D8] to-[#023E8A]">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Tags</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-white uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((contact) => (
                  <tr key={contact.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-semibold text-gray-900">{contact.name}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                        <span>ID: {contact.id.slice(0, 8)}...</span>
                        <AlsoBadge module="contacts" recordId={contact.id} current="contact" />
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.email ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Mail className="w-4 h-4 text-gray-400" />
                          {contact.email}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.phone ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Phone className="w-4 h-4 text-gray-400" />
                          {contact.phone}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.company ? (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Building2 className="w-4 h-4 text-gray-400" />
                          {contact.company}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {contact.tags && contact.tags.length > 0 ? (
                          contact.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              <Tag className="w-3 h-3 mr-1" />
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 text-sm">No tags</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPromoteSource({
                            prefixed_id: `contact:${contact.id}`,
                            name: contact.company || contact.name,
                            source: 'contact',
                          })}
                          className="text-[#FF6B35] border-orange-300 hover:bg-orange-50"
                          title="Promote"
                          data-testid={`promote-contact-${contact.id}`}
                        >
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(contact)}
                          className="text-blue-600 border-blue-600 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(contact.id, contact.name)}
                          className="text-red-600 border-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card className="p-4 mt-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600">
              Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} contacts
            </p>
            
            <div className="flex gap-2">
              <Button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="outline"
              >
                Previous
              </Button>
              
              <span className="px-4 py-2 text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              
              <Button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
              >
                Next
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <CreateContactModal
        isOpen={showCreateModal}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        editingContact={editingContact}
      />

      <PromoteCounterpartyModal
        open={!!promoteSource}
        onClose={() => setPromoteSource(null)}
        source={promoteSource}
        onPromoted={() => fetchContacts()}
      />
    </div>
  );
};

export default Contacts;
